import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { CircleAlert, RefreshCw, Home, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  errorId: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error);
    }

    return {
      hasError: true,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // A crash caught here never reaches Sentry's global handler, so report it ourselves — with the
    // component stack, which is what names the component that broke. A no-op where Sentry isn't set up.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? '' } },
      tags: { errorId: this.state.errorId },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: ''
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="mx-auto mt-16 max-w-screen-sm px-4">
          <Card>
            <CardContent className="pt-6">
              <div className="mb-4 flex items-center gap-4">
                <CircleAlert className="h-10 w-10 shrink-0 text-destructive" />
                <h1 className="text-xl font-semibold">
                  Oops! Something went wrong
                </h1>
              </div>

              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                We're sorry, but something unexpected happened. The error has been logged
                and we'll look into it.
              </div>

              <p className="mb-4 text-sm text-muted-foreground">
                You can try refreshing the page or going back to the home page.
                If the problem persists, please contact support.
              </p>

              {import.meta.env.DEV && (
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={this.toggleDetails}
                    className="mb-2"
                  >
                    {this.state.showDetails ? <ChevronUp /> : <ChevronDown />}
                    {this.state.showDetails ? 'Hide' : 'Show'} Error Details
                  </Button>

                  {this.state.showDetails && (
                    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                      <div>
                        <strong>Error ID:</strong> {this.state.errorId}
                      </div>
                      <div>
                        <strong>Error:</strong> {this.state.error?.toString()}
                      </div>
                      {this.state.error?.stack && (
                        <div className="mt-2">
                          <div>
                            <strong>Stack Trace:</strong>
                          </div>
                          <pre className="overflow-auto rounded bg-gray-100 p-2 text-[0.75rem]">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}
                      {this.state.errorInfo?.componentStack && (
                        <div className="mt-2">
                          <div>
                            <strong>Component Stack:</strong>
                          </div>
                          <pre className="overflow-auto rounded bg-gray-100 p-2 text-[0.75rem]">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>

            <CardFooter className="justify-center gap-2 pb-4">
              <Button onClick={this.handleRefresh}>
                <RefreshCw />
                Refresh Page
              </Button>
              <Button variant="outline" onClick={this.handleGoHome}>
                <Home />
                Go to Home
              </Button>
              {import.meta.env.DEV && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={this.handleReset}
                  title="Reset error boundary (dev only)"
                >
                  <Bug />
                </Button>
              )}
            </CardFooter>
          </Card>

          <p className="mt-4 block text-center text-xs text-muted-foreground">
            Error ID: {this.state.errorId}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
